/* ============================================================
   Trasse Families — refuel declarations, the web half.

   A declaration is EVIDENCE, not an editable note. The parent may move it
   through review and nothing else: `firestore.rules` refuses any write that
   touches `amountKES` or `driverId` after the create, from either side. So this
   hook offers exactly two actions and no "edit".

   The counting rule, which both Analytics and Reports depend on: only a
   CONFIRMED, un-voided declaration is money spent. A pending one is a claim
   nobody has agreed to; a disputed one is a claim the parent actively
   questions. Folding either into "you spent KES 24,000 this month" would put a
   number in front of a household that nobody stands behind.
   ============================================================ */
import { useCallback, useEffect, useState } from 'react';
import {
  collection, doc, limit, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where,
} from 'firebase/firestore';
import { auth, db } from '../../firebase';

/** A household refuels a few times a month; 100 is years of history. */
export const REFUELS_LIMIT = 100;

export type RefuelStatus = 'pending_parent' | 'confirmed' | 'disputed';

export interface FamilyRefuel {
  id: string;
  amountKES: number;
  status: RefuelStatus;
  voided: boolean;
  stationName: string;
  declaredAt: Date | null;
  disputeNote: string;
}

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') return (v as { toDate: () => Date }).toDate();
  if (v instanceof Date) return v;
  return null;
}

/** Anything we do not recognise is still waiting on the parent — an unknown
 *  status must never be read as agreed, or a future value silently promotes a
 *  claim into the household's spend total. Mirrors refuelStatusFromWire in
 *  pear_mobile/lib/services/family/family_refuel.dart. */
function toStatus(v: unknown): RefuelStatus {
  const s = String(v || '');
  return s === 'confirmed' || s === 'disputed' ? s : 'pending_parent';
}

export function countsTowardSpend(r: FamilyRefuel): boolean {
  return !r.voided && r.status === 'confirmed';
}

export function needsParent(r: FamilyRefuel): boolean {
  return !r.voided && r.status === 'pending_parent';
}

export function confirmedSpend(rows: FamilyRefuel[]): number {
  return rows.reduce((sum, r) => (countsTowardSpend(r) ? sum + r.amountKES : sum), 0);
}

export function useFamilyRefuels(orgCode: string) {
  const [refuels, setRefuels] = useState<FamilyRefuel[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgCode) { setLoading(false); return; }
    const unsub = onSnapshot(
      query(
        collection(db, 'familyRefuelEvents'),
        where('orgCode', '==', orgCode),
        orderBy('declaredAt', 'desc'),
        limit(REFUELS_LIMIT),
      ),
      (snap) => {
        const rows: FamilyRefuel[] = [];
        snap.forEach((d) => {
          const x = d.data() as Record<string, unknown>;
          const amount = Number(x.amountKES);
          rows.push({
            id: d.id,
            amountKES: Number.isFinite(amount) ? amount : 0,
            status: toStatus(x.status),
            voided: x.voided === true,
            stationName: String(x.stationName || ''),
            declaredAt: toDate(x.declaredAt) || toDate(x.createdAt),
            disputeNote: String(x.disputeNote || ''),
          });
        });
        setRefuels(rows);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, [orgCode]);

  const confirm = useCallback(async (id: string) => {
    await updateDoc(doc(db, 'familyRefuelEvents', id), {
      status: 'confirmed',
      confirmedBy: auth.currentUser?.uid || null,
      confirmedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }, []);

  const dispute = useCallback(async (id: string, note: string) => {
    await updateDoc(doc(db, 'familyRefuelEvents', id), {
      status: 'disputed',
      disputeNote: (note || '').trim(),
      confirmedBy: auth.currentUser?.uid || null,
      updatedAt: serverTimestamp(),
    });
  }, []);

  return { refuels, loading, confirm, dispute };
}
